/* eslint-disable sonarjs/no-duplicated-branches */
import {
  BOAT_ABOUT_STEP,
  BOAT_FIND_STEP,
} from "Components/Features/CheckPrice/Products/RiskProducts/VehicleProducts/Boat";

import { BoatProductDetailsFormValues } from "Components/Features/CheckPrice/Products/RiskProducts/VehicleProducts/Boat/ProductDetails/boatProductDetailsInterfaces";
import { BoatProductIntroFormValues } from "Components/Features/CheckPrice/Products/RiskProducts/VehicleProducts/Boat/ProductIntro/boatProductIntroInterfaces";
import { DamageRecentYears } from "Components/Form/RecentDamages/recentDamagesInterfaces";
import { removeAllNonDigits } from "Formatting/index";
import { BooleanFormType } from "Store/interfaces/FormTypes";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { BoatType } from "Store/interfaces/products/checkout/riskProducts/BoatProduct";
import { mapFormToInjuries } from "Store/priceCalculation/productMappers/common/injuriesMapper";
import { formBooleanToBoolean } from "Utils/formUtils";

export interface BoatProductBackend {
  name: INSURANCE_NAME_VALUE;
  coverageGroup: string;
  coverageAmount?: number;

  boatType: string;
  modelYear?: number;
  brandAndModel?: string;
  engineBrand?: string;
  engineHorsePower?: number;
  engineTopSpeed?: number;
  lengthInFeet?: number;

  numberOfYearsAsOwner: number;
  hullAndEngineIdMarked: boolean;
  memberOfRedningsselskapetCoastGuard: boolean;
  usedByYoungerDriver: boolean;
  harbourSecurity: boolean;
  trackingSystem: boolean;
  boatNotPristine: boolean;

  harbourZipCode?: string;
  registrationNumber?: string;
  registeredInSmaabaatregisteret?: boolean;
  motorNumber?: string;

  damageCountRecentYears: number;
  yearOfLastDamage?: number;
}

export const mapToCommerceBoat = (
  data: Record<string, any>,
): BoatProductBackend => {
  const boatIntroData: BoatProductIntroFormValues =
    data.formData[BOAT_FIND_STEP.id];
  const boatDetailsData: BoatProductDetailsFormValues =
    data.formData[BOAT_ABOUT_STEP.id];

  return boatProductFor({
    data,
    boatIntroData,
    boatDetailsData,
  });
};

const boatProductFor = ({
  data,
  boatIntroData,
  boatDetailsData,
}: {
  data: Record<string, any>;
  boatIntroData: BoatProductIntroFormValues;
  boatDetailsData: BoatProductDetailsFormValues;
}): BoatProductBackend => {
  const boatType = boatIntroData.boatType as BoatType;

  switch (boatType) {
    case BoatType.MOTOR_BOAT_INBOARDS:
    case BoatType.SAILBOAT:
    case BoatType.MOTOR_BOAT_OUTBOARDS:
      return {
        name: INSURANCE_NAME_VALUE.BOAT,
        coverageGroup: data.coverage,
        coverageAmount: Number(
          removeAllNonDigits(String(boatIntroData.coverageAmount)),
        ),

        boatType: boatIntroData.boatType,
        modelYear: boatIntroData.modelYear,
        brandAndModel: boatIntroData.brandAndModel,
        lengthInFeet: boatIntroData.lengthInFeet,
        engineBrand: boatIntroData.engineBrand,
        engineHorsePower: boatIntroData.engineHorsePower,
        engineTopSpeed: boatIntroData.engineTopSpeed,

        numberOfYearsAsOwner: Number(boatIntroData.numberOfYearsAsOwner),
        hullAndEngineIdMarked: formBooleanToBoolean(
          boatDetailsData.hullAndEngineIdMarked,
        ),
        memberOfRedningsselskapetCoastGuard: formBooleanToBoolean(
          boatDetailsData.memberOfRedningsselskapetCoastGuard,
        ),
        usedByYoungerDriver: formBooleanToBoolean(
          boatDetailsData.usedByYoungerDriver,
        ),
        harbourSecurity: formBooleanToBoolean(boatDetailsData.harbourSecurity),
        trackingSystem: formBooleanToBoolean(boatDetailsData.trackingSystem),
        boatNotPristine: formBooleanToBoolean(boatIntroData.boatNotPristine),

        harbourZipCode: boatDetailsData.postNumber,
        registeredInSmaabaatregisteret: formBooleanToBoolean(
          boatIntroData.registeredInSmaabaatregisteret,
        ),
        registrationNumber:
          boatIntroData.registeredInSmaabaatregisteret === BooleanFormType.TRUE
            ? boatIntroData.registrationNumber
            : undefined,

        motorNumber:
          boatIntroData.registeredInSmaabaatregisteret === BooleanFormType.TRUE
            ? boatIntroData.motorNumber
            : undefined,
        ...mapFormToInjuries(
          boatIntroData.injuries as DamageRecentYears,
          boatIntroData.injuryYear,
        ),
      };
    case BoatType.SMALL_BOAT:
      return {
        name: INSURANCE_NAME_VALUE.BOAT,
        coverageGroup: data.coverage,
        coverageAmount: Number(
          removeAllNonDigits(String(boatIntroData.coverageAmount)),
        ),

        boatType: boatIntroData.boatType,

        numberOfYearsAsOwner: Number(boatIntroData.numberOfYearsAsOwner),
        hullAndEngineIdMarked: false,
        memberOfRedningsselskapetCoastGuard: false,
        usedByYoungerDriver: false,
        harbourSecurity: false,
        trackingSystem: false,
        boatNotPristine: false,

        ...mapFormToInjuries(
          boatDetailsData.injuries as DamageRecentYears,
          boatDetailsData.injuryYear,
        ),
      };
  }
};
